import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import OpenOrdersTable from './components/OpenOrdersTable';
import OrderHistoryTable from './components/OrderHistoryTable';
import OrderFilters from './components/OrderFilters';
import BulkActions from './components/BulkActions';
import OrderStats from './components/OrderStats';

const OrderManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('open');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: 'all',
    tradingPair: 'all',
    orderType: 'all',
    status: 'all',
    searchQuery: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc'
  });

  // Mock data for open orders
  const openOrders = [
    {
      id: 'ORD-001',
      type: 'limit',
      side: 'buy',
      pair: 'BTC/USDT',
      quantity: 0.5,
      price: 42500.00,
      filled: 0.2,
      remaining: 0.3,
      total: 21250.00,
      status: 'partial',
      timestamp: new Date(Date.now() - 3600000),
      timeInForce: 'GTC',
      stopPrice: null,
      fees: 0.1
    },
    {
      id: 'ORD-002',
      type: 'stop-limit',
      side: 'sell',
      pair: 'ETH/USDT',
      quantity: 2.0,
      price: 2800.00,
      filled: 0,
      remaining: 2.0,
      total: 5600.00,
      status: 'pending',
      timestamp: new Date(Date.now() - 1800000),
      timeInForce: 'GTC',
      stopPrice: 2850.00,
      fees: 0.1
    },
    {
      id: 'ORD-003',
      type: 'market',
      side: 'buy',
      pair: 'ADA/USDT',
      quantity: 1000,
      price: 0.45,
      filled: 1000,
      remaining: 0,
      total: 450.00,
      status: 'executing',
      timestamp: new Date(Date.now() - 300000),
      timeInForce: 'IOC',
      stopPrice: null,
      fees: 0.1
    },
    {
      id: 'ORD-004',
      type: 'limit',
      side: 'sell',
      pair: 'DOT/USDT',
      quantity: 50,
      price: 8.25,
      filled: 0,
      remaining: 50,
      total: 412.50,
      status: 'pending',
      timestamp: new Date(Date.now() - 7200000),
      timeInForce: 'GTC',
      stopPrice: null,
      fees: 0.1
    },
    {
      id: 'ORD-005',
      type: 'trailing-stop',
      side: 'sell',
      pair: 'SOL/USDT',
      quantity: 10,
      price: 95.00,
      filled: 0,
      remaining: 10,
      total: 950.00,
      status: 'pending',
      timestamp: new Date(Date.now() - 5400000),
      timeInForce: 'GTC',
      stopPrice: 98.50,
      fees: 0.1
    }
  ];

  // Mock data for order history
  const orderHistory = [
    {
      id: 'ORD-H001',
      type: 'limit',
      side: 'buy',
      pair: 'BTC/USDT',
      quantity: 1.0,
      price: 41800.00,
      filled: 1.0,
      total: 41800.00,
      status: 'filled',
      timestamp: new Date(Date.now() - 86400000),
      executionTime: new Date(Date.now() - 86395000),
      fees: 41.80,
      avgPrice: 41800.00
    },
    {
      id: 'ORD-H002',
      type: 'market',
      side: 'sell',
      pair: 'ETH/USDT',
      quantity: 5.0,
      price: 2750.00,
      filled: 5.0,
      total: 13750.00,
      status: 'filled',
      timestamp: new Date(Date.now() - 172800000),
      executionTime: new Date(Date.now() - 172795000),
      fees: 13.75,
      avgPrice: 2750.00
    },
    {
      id: 'ORD-H003',
      type: 'limit',
      side: 'buy',
      pair: 'ADA/USDT',
      quantity: 2000,
      price: 0.42,
      filled: 0,
      total: 840.00,
      status: 'cancelled',
      timestamp: new Date(Date.now() - 259200000),
      executionTime: null,
      fees: 0,
      avgPrice: 0
    },
    {
      id: 'ORD-H004',
      type: 'stop-loss',
      side: 'sell',
      pair: 'DOT/USDT',
      quantity: 100,
      price: 7.80,
      filled: 100,
      total: 780.00,
      status: 'filled',
      timestamp: new Date(Date.now() - 345600000),
      executionTime: new Date(Date.now() - 345580000),
      fees: 0.78,
      avgPrice: 7.80
    },
    {
      id: 'ORD-H005',
      type: 'limit',
      side: 'sell',
      pair: 'SOL/USDT',
      quantity: 20,
      price: 92.50,
      filled: 15,
      total: 1387.50,
      status: 'partially_filled',
      timestamp: new Date(Date.now() - 432000000),
      executionTime: new Date(Date.now() - 431900000),
      fees: 13.88,
      avgPrice: 92.50
    }
  ];

  const tabs = [
    { id: 'open', label: 'Open Orders', count: openOrders.length, icon: 'Clock' },
    { id: 'history', label: 'Order History', count: orderHistory.length, icon: 'History' }
  ];

  const tradingPairs = ['BTC/USDT', 'ETH/USDT', 'ADA/USDT', 'DOT/USDT', 'SOL/USDT'];
  const orderTypes = ['market', 'limit', 'stop-loss', 'stop-limit', 'trailing-stop'];

  // Filter and sort data
  const filteredData = useMemo(() => {
    const data = activeTab === 'open' ? openOrders : orderHistory;
    
    let filtered = data.filter(order => {
      const matchesDateRange = filters.dateRange === 'all' || 
        (filters.dateRange === '24h' && order.timestamp > new Date(Date.now() - 86400000)) ||
        (filters.dateRange === '7d' && order.timestamp > new Date(Date.now() - 604800000)) ||
        (filters.dateRange === '30d' && order.timestamp > new Date(Date.now() - 2592000000));
      
      const matchesPair = filters.tradingPair === 'all' || order.pair === filters.tradingPair;
      const matchesType = filters.orderType === 'all' || order.type === filters.orderType;
      const matchesStatus = filters.status === 'all' || order.status === filters.status;
      const matchesSearch = !filters.searchQuery || 
        order.pair.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        order.id.toLowerCase().includes(filters.searchQuery.toLowerCase());

      return matchesDateRange && matchesPair && matchesType && matchesStatus && matchesSearch;
    });

    // Sort data
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [activeTab, filters, sortConfig, openOrders, orderHistory]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredData.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredData.map(order => order.id));
    }
  };

  const handleBulkCancel = () => {
    // Mock bulk cancel functionality
    console.log('Cancelling orders:', selectedOrders);
    setSelectedOrders([]);
  };

  const handleExport = () => {
    // Mock export functionality
    const dataToExport = filteredData.map(order => ({
      'Order ID': order.id,
      'Type': order.type,
      'Side': order.side,
      'Pair': order.pair,
      'Quantity': order.quantity,
      'Price': order.price,
      'Status': order.status,
      'Timestamp': order.timestamp.toISOString()
    }));
    
    console.log('Exporting data:', dataToExport);
  };

  const handleReorder = (order) => {
    // Mock reorder functionality - navigate to trading dashboard with pre-filled order
    navigate('/trading-dashboard', { 
      state: { 
        prefilledOrder: {
          pair: order.pair,
          type: order.type,
          side: order.side,
          quantity: order.quantity,
          price: order.price
        }
      }
    });
  };

  useEffect(() => {
    // Reset selected orders when changing tabs
    setSelectedOrders([]);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-text-secondary mb-4">
            <button 
              onClick={() => navigate('/trading-dashboard')}
              className="hover:text-primary transition-colors duration-trading-fast"
            >
              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-text-primary">Order Management</span>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-heading-bold text-text-primary mb-2">Order Management</h1>
              <p className="text-text-secondary font-body-normal">
                Monitor, modify, and track all your trading orders in one place
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExport}
                className="flex items-center space-x-2 px-4 py-2 bg-secondary-100 text-text-primary rounded-trading hover:bg-secondary-200 transition-colors duration-trading-fast"
              >
                <Icon name="Download" size={16} />
                <span className="font-body-medium text-sm">Export</span>
              </button>
              
              <button
                onClick={() => navigate('/trading-dashboard')}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
              >
                <Icon name="Plus" size={16} />
                <span className="font-body-medium text-sm">New Order</span>
              </button>
            </div>
          </div>
        </div>

        {/* Order Statistics */}
        <OrderStats 
          openOrders={openOrders}
          orderHistory={orderHistory}
        />

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-border">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-body-medium text-sm transition-colors duration-trading-fast ${
                    activeTab === tab.id
                      ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-caption-medium ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary' :'bg-secondary-100 text-text-secondary'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Filters */}
        <OrderFilters
          filters={filters}
          onFiltersChange={setFilters}
          tradingPairs={tradingPairs}
          orderTypes={orderTypes}
          activeTab={activeTab}
        />

        {/* Bulk Actions */}
        {activeTab === 'open' && selectedOrders.length > 0 && (
          <BulkActions
            selectedCount={selectedOrders.length}
            onBulkCancel={handleBulkCancel}
            onClearSelection={() => setSelectedOrders([])}
          />
        )}

        {/* Orders Table */}
        <div className="bg-surface rounded-trading border border-border shadow-trading-card">
          {activeTab === 'open' ? (
            <OpenOrdersTable
              orders={filteredData}
              selectedOrders={selectedOrders}
              onSelectOrder={handleSelectOrder}
              onSelectAll={handleSelectAll}
              onSort={handleSort}
              sortConfig={sortConfig}
              onReorder={handleReorder}
            />
          ) : (
            <OrderHistoryTable
              orders={filteredData}
              onSort={handleSort}
              sortConfig={sortConfig}
              onReorder={handleReorder}
            />
          )}
        </div>

        {/* Empty State */}
        {filteredData.length === 0 && (
          <div className="bg-surface rounded-trading border border-border p-12 text-center">
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={32} className="text-text-secondary" />
            </div>
            <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
              No {activeTab === 'open' ? 'Open Orders' : 'Order History'} Found
            </h3>
            <p className="text-text-secondary font-body-normal mb-6">
              {activeTab === 'open' ?'You don\'t have any active orders at the moment.' :'No orders match your current filter criteria.'
              }
            </p>
            {activeTab === 'open' && (
              <button
                onClick={() => navigate('/trading-dashboard')}
                className="px-6 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
              >
                Place Your First Order
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;